export const ROLE_IDS = {
  ADMIN: 1,
  PROJECT_MANAGER: 2,
  SITE_SUPERVISOR: 3,
  WORKER: 4,
  ENGINEER: 5,
  ACCOUNTANT: 6,
};

export const PERMISSION_KEYS = [
  'can_manage_projects',
  'can_manage_tasks',
  'can_manage_puantaj',
  'can_approve_puantaj',
  'can_manage_daily_reports',
  'can_manage_photos',
  'can_manage_resources',
  'can_manage_teams',
  'can_manage_users',
  'can_manage_documents',
  'can_manage_finance',
  'can_manage_change_orders',
  'can_view_reports',
];

const buildEmptyPermissions = () =>
  PERMISSION_KEYS.reduce((acc, key) => {
    acc[key] = false;
    return acc;
  }, {});

const DEFAULT_PERMISSIONS_BY_ROLE = {
  [ROLE_IDS.ADMIN]: PERMISSION_KEYS.reduce((acc, key) => {
    acc[key] = true;
    return acc;
  }, {}),
  [ROLE_IDS.PROJECT_MANAGER]: {
    can_manage_projects: true,
    can_manage_tasks: true,
    can_manage_puantaj: true,
    can_approve_puantaj: true,
    can_manage_daily_reports: true,
    can_manage_photos: true,
    can_manage_resources: true,
    can_manage_teams: true,
    can_manage_users: true,
    can_manage_documents: true,
    can_manage_finance: false,
    can_manage_change_orders: true,
    can_view_reports: true,
  },
  [ROLE_IDS.SITE_SUPERVISOR]: {
    can_manage_projects: false,
    can_manage_tasks: true,
    can_manage_puantaj: true,
    can_approve_puantaj: true,
    can_manage_daily_reports: true,
    can_manage_photos: true,
    can_manage_resources: true,
    can_manage_teams: true,
    can_manage_users: false,
    can_manage_documents: true,
    can_manage_finance: false,
    can_manage_change_orders: true,
    can_view_reports: true,
  },
  [ROLE_IDS.WORKER]: {
    can_manage_projects: false,
    can_manage_tasks: false,
    can_manage_puantaj: true,
    can_approve_puantaj: false,
    can_manage_daily_reports: false,
    can_manage_photos: false,
    can_manage_resources: false,
    can_manage_teams: false,
    can_manage_users: false,
    can_manage_documents: false,
    can_manage_finance: false,
    can_manage_change_orders: false,
    can_view_reports: true,
  },
  [ROLE_IDS.ENGINEER]: {
    can_manage_projects: false,
    can_manage_tasks: true,
    can_manage_puantaj: false,
    can_approve_puantaj: false,
    can_manage_daily_reports: true,
    can_manage_photos: true,
    can_manage_resources: false,
    can_manage_teams: false,
    can_manage_users: false,
    can_manage_documents: true,
    can_manage_finance: false,
    can_manage_change_orders: true,
    can_view_reports: true,
  },
  [ROLE_IDS.ACCOUNTANT]: {
    can_manage_projects: false,
    can_manage_tasks: false,
    can_manage_puantaj: false,
    can_approve_puantaj: false,
    can_manage_daily_reports: false,
    can_manage_photos: false,
    can_manage_resources: false,
    can_manage_teams: false,
    can_manage_users: false,
    can_manage_documents: false,
    can_manage_finance: true,
    can_manage_change_orders: false,
    can_view_reports: false,
  },
};

export const normalizePermissions = (user, rawPermissions = {}) => {
  const roleId = Number(user?.role_id || 0);
  const basePermissions = buildEmptyPermissions();
  const defaultPermissions = DEFAULT_PERMISSIONS_BY_ROLE[roleId] || {};
  const explicitPermissions = {};

  PERMISSION_KEYS.forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(rawPermissions || {}, key)) {
      explicitPermissions[key] = Boolean(rawPermissions[key]);
    }
  });

  return {
    ...basePermissions,
    ...defaultPermissions,
    ...explicitPermissions,
  };
};

export const hasEffectivePermission = (user, permissions, key) => {
  if (!key) return false;
  const normalized = normalizePermissions(user, permissions);
  return Boolean(normalized[key]);
};

export const isAccountant = (user) => Number(user?.role_id) === ROLE_IDS.ACCOUNTANT;

export const canAccessAccountingPanel = (user, permissions) =>
  [ROLE_IDS.ADMIN, ROLE_IDS.ACCOUNTANT].includes(Number(user?.role_id)) &&
  hasEffectivePermission(user, permissions, 'can_manage_finance');

export const canManageUserDirectory = (user, permissions) =>
  [ROLE_IDS.ADMIN, ROLE_IDS.PROJECT_MANAGER].includes(Number(user?.role_id)) &&
  hasEffectivePermission(user, permissions, 'can_manage_users');

export const canAccessAdminPermissions = (user) => Number(user?.role_id) === ROLE_IDS.ADMIN;

export const canManageNotificationPreferences = (user) =>
  [ROLE_IDS.ADMIN, ROLE_IDS.PROJECT_MANAGER].includes(Number(user?.role_id));
